import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, PackageCheck, Ban, CheckCircle2 } from 'lucide-react';
import { formatWeight } from '@vj/shared';
import { api, ApiClientError } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { TextAreaField } from '../../components/ui/Field.jsx';
import { ThermalPrintButton } from '../../components/ThermalPrintButton.jsx';
import { PdfActions } from '../../components/PdfActions.jsx';

const STATUS_LABEL = {
  pending: 'Pending',
  partially_returned: 'Partially returned',
  fully_returned: 'Fully returned',
  cancelled: 'Cancelled',
};

const STATUS_TONE = {
  pending: 'border-muted text-muted',
  partially_returned: 'border-warning text-warning',
  fully_returned: 'border-success text-success',
  cancelled: 'border-danger text-danger',
};

const ISSUED_TO_LABEL = { karigar: 'Aasari', employee: 'Employee', customer: 'Customer' };

export default function IssueViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [actionError, setActionError] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [cancelError, setCancelError] = useState(null);

  const { data: voucher, isLoading } = useQuery({
    queryKey: ['issues', id],
    queryFn: () => api.get(`/issue/${id}`),
  });

  const historyQuery = useQuery({
    queryKey: ['issues', id, 'receipts'],
    queryFn: () => api.get(`/issue/${id}/receipts`),
  });

  const cancelMutation = useMutation({
    mutationFn: () => api.post(`/issue/${id}/cancel`, { reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['issues'] });
      setCancelOpen(false);
    },
    onError: (err) => setCancelError(err instanceof ApiClientError ? err.message : 'Could not cancel this voucher.'),
  });

  if (isLoading || !voucher) return null;

  const canReceive = ['pending', 'partially_returned'].includes(voucher.status);
  const canCancel = ['pending', 'partially_returned'].includes(voucher.status);
  const receipts = historyQuery.data?.receipts ?? [];

  return (
    <div className="flex flex-col gap-5 pb-24">
      <div className="flex items-center gap-3">
        <Link to="/issue-receipt" className="flex h-touch w-touch items-center justify-center rounded-card hover:bg-surface-2">
          <ArrowLeft size={20} aria-hidden="true" />
        </Link>
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">{voucher.voucher_no}</h1>
          <p className="text-sm text-muted">{new Date(voucher.issue_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
        </div>
        <span className={`ml-auto rounded-full border px-3 py-1 text-xs font-medium uppercase tracking-wide ${STATUS_TONE[voucher.status]}`}>
          {STATUS_LABEL[voucher.status]}
        </span>
      </div>

      {voucher.status === 'cancelled' ? (
        <p className="rounded-card border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
          Cancelled {new Date(voucher.cancelled_at).toLocaleDateString('en-IN')}: {voucher.cancel_reason}
        </p>
      ) : null}

      <section className="card grid grid-cols-1 gap-4 p-5 sm:grid-cols-2">
        <div>
          <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">Issued to</h2>
          <p className="font-medium">{voucher.issued_to_name}</p>
          <p className="text-sm text-muted">{ISSUED_TO_LABEL[voucher.issued_to_type] ?? voucher.issued_to_type}</p>
          {voucher.issued_to_address ? <p className="text-sm text-muted">{voucher.issued_to_address}</p> : null}
        </div>
        <div>
          <h2 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">Purpose / Return</h2>
          <p className="font-medium">{voucher.purpose || 'Not specified'}</p>
          {voucher.expected_return_date ? (
            <p className="text-sm text-muted">Expected back {new Date(voucher.expected_return_date).toLocaleDateString('en-IN')}</p>
          ) : null}
        </div>
      </section>

      <section className="card p-5">
        <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted">Items</h2>
        <div className="table-scroll">
          <table className="w-full min-w-[680px] text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted">
                <th className="py-2 pr-3">Item</th>
                <th className="py-2 pr-3">Purity</th>
                <th className="py-2 pr-3 text-right">Qty issued</th>
                <th className="py-2 pr-3 text-right">Net weight</th>
                <th className="py-2 pr-3 text-right">Pending qty</th>
                <th className="py-2 text-right">Pending weight</th>
              </tr>
            </thead>
            <tbody>
              {voucher.items.map((item) => (
                <tr key={item.item_id} className="border-b border-border last:border-0">
                  <td className="py-2 pr-3 font-medium">
                    {item.item_name}
                    {item.serial_number ? <span className="text-muted"> · {item.serial_number}</span> : null}
                  </td>
                  <td className="py-2 pr-3">{item.purity_label || '—'}</td>
                  <td className="tabular py-2 pr-3 text-right">{item.quantity}</td>
                  <td className="tabular py-2 pr-3 text-right">{formatWeight(item.net_weight)}</td>
                  <td className={`tabular py-2 pr-3 text-right ${item.pending_quantity > 0 ? 'text-warning' : 'text-success'}`}>{item.pending_quantity}</td>
                  <td className={`tabular py-2 text-right ${item.pending_quantity > 0 ? 'text-warning' : 'text-success'}`}>{formatWeight(item.pending_net_weight)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card grid grid-cols-1 gap-4 p-5 sm:grid-cols-2">
        <dl className="flex flex-col gap-1.5 text-sm">
          <div className="flex justify-between"><dt className="text-muted">Total quantity</dt><dd className="tabular">{voucher.total_quantity}</dd></div>
          <div className="flex justify-between"><dt className="text-muted">Total gross weight</dt><dd className="tabular">{formatWeight(voucher.total_gross_weight)}</dd></div>
          <div className="flex justify-between font-semibold"><dt>Total net weight</dt><dd className="tabular">{formatWeight(voucher.total_net_weight)}</dd></div>
        </dl>
        <div>
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">Receipt history</h2>
          {receipts.length === 0 ? (
            <p className="text-sm text-muted">Nothing has come back against this voucher yet.</p>
          ) : (
            <ol className="flex flex-col gap-2 text-sm">
              {receipts.map((r) => (
                <li key={r.receipt_id}>
                  <button type="button" onClick={() => navigate(`/issue-receipt/receipts/${r.receipt_id}`)} className={`flex w-full justify-between gap-2 text-left hover:text-primary ${r.status === 'cancelled' ? 'opacity-60' : ''}`}>
                    <span>
                      {r.voucher_no}
                      {r.status === 'cancelled' ? <span className="text-danger"> · cancelled</span> : r.has_mismatch ? <span className="text-danger"> · mismatch</span> : null}
                    </span>
                    <span className="shrink-0 text-muted">{new Date(r.receipt_date).toLocaleDateString('en-IN')}</span>
                  </button>
                </li>
              ))}
            </ol>
          )}
        </div>
      </section>

      {actionError ? (
        <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
          {actionError}
        </p>
      ) : null}

      <div className="raised fixed inset-x-0 bottom-0 z-20 flex items-center gap-2 border-t border-border bg-surface px-4 py-3 shadow-raised sm:static sm:border-0 sm:bg-transparent sm:p-0 sm:shadow-none">
        <PdfActions
          path={`/issue/${id}/pdf`}
          documentLabel={`Issue voucher ${voucher.voucher_no}`}
          fileBaseName={voucher.voucher_no}
          onError={setActionError}
        />
        <ThermalPrintButton printPath={`/issue/${id}`} />
        {canReceive ? (
          <Button onClick={() => navigate(`/issue-receipt/${id}/receive`)} className="flex-1 sm:flex-none">
            <PackageCheck size={17} aria-hidden="true" /> Record receipt
          </Button>
        ) : null}
        {canCancel ? (
          <Button variant="danger" onClick={() => { setCancelError(null); setCancelOpen(true); }} className="flex-1 sm:flex-none">
            <Ban size={17} aria-hidden="true" /> Cancel
          </Button>
        ) : null}
      </div>

      <Modal open={cancelOpen} onClose={() => setCancelOpen(false)} title="Cancel this voucher?">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted">
            The voucher number <b>{voucher.voucher_no}</b> stays reserved and is never reused. Any weight still
            outstanding on this voucher is reversed back into stock; whatever has already been received back is
            unaffected.
          </p>
          <TextAreaField label="Reason" required value={reason} onChange={(e) => setReason(e.target.value)} />
          {cancelError ? (
            <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">
              {cancelError}
            </p>
          ) : null}
          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setCancelOpen(false)}>Keep voucher</Button>
            <Button
              variant="danger"
              disabled={reason.trim().length < 3}
              loading={cancelMutation.isPending}
              onClick={() => cancelMutation.mutate()}
            >
              <CheckCircle2 size={16} aria-hidden="true" /> Confirm cancellation
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
